/**
 * F-149/F-150: a plan's import-step account, one line per step it ran —
 * who handled it and how it went. Shown under the plan, so how well each
 * allocated model does its step is visible on every run. Numbered in the
 * owner's order: populate, split, translate, align.
 */
import type { AgentImportStep, AgentImportStepKind, AgentPlan } from "@kola/shared";

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function cost(step: AgentImportStep): string {
  const parts = [plural(step.calls, "call"), `${(step.ms / 1000).toFixed(1)}s`];
  if (step.tokensIn !== undefined || step.tokensOut !== undefined) {
    parts.push(`${((step.tokensIn ?? 0) + (step.tokensOut ?? 0)).toLocaleString("en-US")} tokens`);
  }
  return parts.join(" · ");
}

export interface ImportStepLine {
  label: string;
  handler: string;
  detail: string;
  /** Some of the step's items fell back. */
  warn: boolean;
}

const LABELS: Record<AgentImportStepKind, string> = {
  parse: "1. Populate",
  beats: "2. Split into beats",
  translate: "3. Translate",
  align: "4. Align columns",
};

function detail(step: AgentImportStep): string {
  switch (step.step) {
    case "parse":
      return `${plural(step.items, "file row")}, as they are`;
    case "beats":
      return `${step.done} of ${plural(step.items, "row")} cut${step.fallbacks > 0 ? `, ${step.fallbacks} kept whole` : ""} · ${cost(step)}`;
    case "translate":
      return `${step.done} of ${plural(step.items, "row")} translated${step.fallbacks > 0 ? `, ${step.fallbacks} unusable` : ""} · ${cost(step)}`;
    case "align":
      return step.calls === 0
        ? "nothing to divide"
        : `${step.done} of ${plural(step.items, "cell")} divided across beats${step.fallbacks > 0 ? `, ${step.fallbacks} left on the first beat row` : ""} · ${cost(step)}`;
  }
}

export function importStepLines(plan: Pick<AgentPlan, "importSteps">): ImportStepLine[] {
  return (plan.importSteps ?? []).map((step) => ({
    label: LABELS[step.step],
    handler: step.handler,
    detail: detail(step),
    warn: step.fallbacks > 0,
  }));
}
